// CallHandlingSections.tsx · v12.10.26 (#T154 + Texts on calls section - its own card, dirty state and Save section) · 2026-10-30 · VoiceSpectrum AI
// CallHandlingSections.tsx · v12.10.23 (#2012 #2028 #2029 Keypad menus section; Silent caller in Conversation flow replaces Max silence retries / Silence final action; order Conversation flow · Recording · Keypad menus · Hold · Processing) · 2026-10-29 · VoiceSpectrum AI
// CallHandlingSections.tsx · v12.10.15 (#1666 Recording › Voicemail details: Smart (AI) | Basic (keywords)) · 2026-10-13 · VoiceSpectrum AI
// CallHandlingSections.tsx · v12.10.11.1 (#1484 filler-phrase language tabs match the standard "English · EN" tabs) · 2026-10-09
// CallHandlingSections.tsx · v12.10.9.4 (#1357 US spelling) · 2026-10-07 · VoiceSpectrum AI
// v12.10.8.1 (#1232): Advanced - listening wait by kind of answer (UI-PROTO-EOS-1232 v1.2): one setting per row for all languages,
//   "Set per language" for any language the practice has on; saved with Conversation flow into the eos.* agent-phrase keys.
// CallHandlingSections.tsx — v12.9.26 (#967): 'Max silence retries' stays on screen with a 'Not used' pill (his instruction) - each card's Ask cap now decides.
// CallHandlingSections.tsx — v12.1.13.0
// Call Handling settings area (4 sections): Conversation flow · Processing Behavior · Hold Behavior
// · Recording. Real GET load + per-section Save/Discard wired to CallHandlingSettingsController, with
// the shared pick-one pill (settings-controls.css), the platform Sound Library filtered by kind, the
// per-language filler-phrase editor, and per-section dirty state reported up to the Settings shell.
// Ported for fidelity from call_handling_only_v1_58.html. All fields apply live.
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { HelpTooltip } from '../components/aiagent/HelpTooltip';
import './settings-controls.css';
import './call-handling.css';
import TextsOnCallsSection, { TextsOnCallsState } from './TextsOnCallsSection';   // v12.10.26 (#T154): Call Handling › Texts on calls

export interface ChSound { code: string; name: string; description: string | null; audioUrl: string | null }
export interface ChFillerCategory { key: string; label: string; hint: string }
export interface ChFillerLang { code: string; name: string }
// filler[scope][lang][categoryKey] = [phrases]
export type ChFiller = Record<string, Record<string, Record<string, string[]>>>;

export interface CallHandlingData {
  speechTimeout: number; speechEndTimeout: string; spellSpeechEndTimeout: string; maxSilenceRetries: number; silenceFinalAction: string;
  operatorDetect: boolean; operatorKeywords: string | null; operatorAction: string; lowConfAction: string; slotNormalization: string;
  dtmfDelay: number; dtmfAccept: string; dtmfAnnounce: string;
  procMode: string; procDelay: number; procSound: string | null;
  holdMode: string; holdDelay: number; holdSound: string | null;
  recEnabled: boolean; storeTranscript: boolean; autoRedact: boolean; recRetention: number; transRetention: number;
  voicemailDetails?: string;   // v12.10.15 (#1666): 'smart' | 'basic'
  redactEntitled: boolean; handoffEnabled: boolean;
  thinkingSounds: ChSound[]; holdSounds: ChSound[];
  procCategories: ChFillerCategory[]; holdCategories: ChFillerCategory[];
  languages: ChFillerLang[];
  filler: ChFiller;
  confidenceFloor: number | null;
  // v12.10.8.1 (#1232): kind -> language -> "auto" | seconds; a missing language = platform default.
  listenWaits: Record<string, Record<string, string>>;
  listenDefaults: Record<string, number>;
  fieldWaits: { label: string; seconds: number }[];
  // v12.10.23 (#2012 #2028 #2029): tenant_settings["callMenus"]. silent is always filled by GET; silentFromWorkflow = not saved here yet.
  callMenus?: ChCallMenus;
  silentFromWorkflow?: boolean;
}
export interface ChMenuKey { key: string; on: boolean; line: Record<string, string> }
export interface ChSilent { turns: number; nothingGiven: string; somethingGiven: string }
export interface ChCallMenus { repeat: ChMenuKey; mainMenu: ChMenuKey; person: ChMenuKey; reservedKey: string; shortMenus: boolean; shortMenuMax: number; silent: ChSilent | null }

export type CallSectionKey = 'conversation' | 'processing' | 'hold' | 'recording' | 'menus' | 'texts';   // v12.10.26 (#T154): + Texts on calls   // v12.10.23 (#2012): + Keypad menus
export type CallDirtyMap = Record<CallSectionKey, boolean>;
export interface CallHandlingState { dirty: CallDirtyMap; anyDirty: boolean; saveAll: () => void; discardAll: () => void; }

const QKEY = ['t2-call-handling'];

const SILENCE_ACTIONS: [string, string][] = [['voicemail', 'Route to voicemail'], ['handoff', 'Handoff'], ['end', 'End call']];
const OPERATOR_ACTIONS: [string, string][] = [['voicemail', 'Route to voicemail'], ['handoff', 'Handoff'], ['end', 'End call']];
const LOWCONF_ACTIONS: [string, string][] = [['repeat_vm', 'Repeat once, then voicemail'], ['repeat_handoff', 'Repeat once, then handoff'], ['end', 'End call']];
// v12.7.3 (#644): On / Off. 'Always' and 'After silence' are gone - the engine never read this setting at all,
// so neither option ever meant anything, and 'Always' promised a behaviour no screen could deliver. On = a
// key press is heard wherever the workflow supports it, which is the silent keypad backup the section
// already promises in its own sub-note.
const DTMF_ACCEPT: [string, string][] = [['on', 'On'], ['off', 'Off']];
const DTMF_ANNOUNCE: [string, string][] = [['after_silence', 'After silence fallback'], ['on_greeting', 'On greeting'], ['never', 'Never']];
const EOS_MODE: [string, string][] = [['auto', 'Auto (recommended)'], ['custom', 'Custom']];
// v12.7.3 (#641): 'Raw' is the tenant's word for it and it now genuinely means raw - the value the telephony
// provider returned, with no processing. It used to be rewritten to 'fast' inside the extractor.
const SLOTNORM: [string, string][] = [['off', 'Raw (no processing)'], ['fast', 'Fast (recommended)'], ['ai', 'AI assist']];
// v12.10.23 (#2012 #2028 #2029): Keypad menus + Silent caller (CallMenuSettings). "voicemail" is the Leave a Message card.
const SILENT_ENDINGS: [string, string][] = [['end-call', 'End call'], ['voicemail', 'Leave a Message'], ['handoff', 'Transfer to staff']];
const DEFAULT_SILENT: ChSilent = { turns: 2, nothingGiven: 'end-call', somethingGiven: 'voicemail' };
const DEFAULT_MENUS: ChCallMenus = { repeat: { key: '0', on: true, line: {} }, mainMenu: { key: '*', on: true, line: {} }, person: { key: '9', on: true, line: {} }, reservedKey: '#', shortMenus: true, shortMenuMax: 3, silent: null };
const menusOf = (d: CallHandlingData): ChCallMenus => d.callMenus ?? DEFAULT_MENUS;
const silentOf = (d: CallHandlingData): ChSilent => menusOf(d).silent ?? DEFAULT_SILENT;
const menusTuple = (d: CallHandlingData) => { const m = menusOf(d); return [m.repeat, m.mainMenu, m.person, m.reservedKey, m.shortMenus, m.shortMenuMax]; };
const eosSec = (v: string): number => { const n = parseInt(v, 10); return isNaN(n) ? 2 : Math.min(10, Math.max(1, n)); };

export default function CallHandlingSections({ onStateChange }: { onStateChange?: (s: CallHandlingState) => void } = {}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<CallHandlingData>({
    queryKey: QKEY,
    queryFn: () => api.get<CallHandlingData>('/t2/settings/call-handling'),
  });

  const [saved, setSaved] = useState<CallHandlingData | null>(null);
  const [draft, setDraft] = useState<CallHandlingData | null>(null);
  const [textsState, setTextsState] = useState<TextsOnCallsState | null>(null);   // v12.10.26 (#T154): Texts on calls reports its own dirty state
  const [toast, setToast] = useState('');
  const toastTimer = useRef<number | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  function showToast(msg: string) {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = window.setTimeout(() => setToast(''), 3000);
  }

  useEffect(() => {
    if (!data) return;
    setSaved(structuredClone(data));
    setDraft(structuredClone(data));
  }, [data]);

  function onSaved(fresh: CallHandlingData) {
    setSaved(structuredClone(fresh));
    setDraft(structuredClone(fresh));
    queryClient.setQueryData(QKEY, fresh);
  }

  const saveConversation = useMutation({
    mutationFn: (d: CallHandlingData) => api.put<CallHandlingData>('/t2/settings/call-handling/conversation-flow', {
      speechTimeout: d.speechTimeout, speechEndTimeout: d.speechEndTimeout, spellSpeechEndTimeout: d.spellSpeechEndTimeout, maxSilenceRetries: d.maxSilenceRetries, silenceFinalAction: d.silenceFinalAction,
      operatorDetect: d.operatorDetect, operatorKeywords: d.operatorKeywords, operatorAction: d.operatorAction,
      lowConfAction: d.lowConfAction, slotNormalization: d.slotNormalization, dtmfDelay: d.dtmfDelay, dtmfAccept: d.dtmfAccept, dtmfAnnounce: d.dtmfAnnounce,
      listenWaits: d.listenWaits ?? {},   // v12.10.8.1 (#1232)
      // v12.10.23 (#2029): sent once it is the practice's own or was changed; otherwise the workflow's rule stays in charge.
      silent: d.silentFromWorkflow && saved && JSON.stringify(silentOf(d)) === JSON.stringify(silentOf(saved)) ? undefined : silentOf(d),
    }),
    onSuccess: onSaved,
  });
  // v12.10.23 (#2012 #2028): Keypad menus - keys + short menus; the Silent caller is saved with Conversation flow.
  const saveMenus = useMutation({
    mutationFn: (d: CallHandlingData) => { const m = menusOf(d); return api.put<CallHandlingData>('/t2/settings/call-handling/keypad-menus', {
      repeat: m.repeat, mainMenu: m.mainMenu, person: m.person, reservedKey: m.reservedKey, shortMenus: m.shortMenus, shortMenuMax: m.shortMenuMax,
    }); },
    onSuccess: onSaved,
  });
  const saveProcessing = useMutation({
    mutationFn: (d: CallHandlingData) => api.put<CallHandlingData>('/t2/settings/call-handling/processing', {
      mode: d.procMode, delay: d.procDelay, sound: d.procSound, filler: d.filler.proc,
    }),
    onSuccess: onSaved,
  });
  const saveHold = useMutation({
    mutationFn: (d: CallHandlingData) => api.put<CallHandlingData>('/t2/settings/call-handling/hold', {
      mode: d.holdMode, delay: d.holdDelay, sound: d.holdSound, filler: d.filler.hold,
    }),
    onSuccess: onSaved,
  });
  const saveRecording = useMutation({
    mutationFn: (d: CallHandlingData) => api.put<CallHandlingData>('/t2/settings/call-handling/recording', {
      recEnabled: d.recEnabled, storeTranscript: d.storeTranscript, autoRedact: d.autoRedact,
      recRetention: d.recRetention, transRetention: d.transRetention, voicemailDetails: d.voicemailDetails ?? 'smart',   // v12.10.15 (#1666)
    }),
    onSuccess: onSaved,
  });

  // Report per-section dirty + save/discard-all up to the Settings shell (mirrors AiAgentSections).
  useEffect(() => {
    if (!draft || !saved || !onStateChange) return;
    const eqx = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const dirty: CallDirtyMap = {
      conversation: !eqx(convTuple(draft), convTuple(saved)),
      processing: !eqx([draft.procMode, draft.procDelay, draft.procSound, draft.filler.proc], [saved.procMode, saved.procDelay, saved.procSound, saved.filler.proc]),
      hold: !eqx([draft.holdMode, draft.holdDelay, draft.holdSound, draft.filler.hold], [saved.holdMode, saved.holdDelay, saved.holdSound, saved.filler.hold]),
      recording: !eqx([draft.recEnabled, draft.storeTranscript, draft.autoRedact, draft.recRetention, draft.transRetention, draft.voicemailDetails], [saved.recEnabled, saved.storeTranscript, saved.autoRedact, saved.recRetention, saved.transRetention, saved.voicemailDetails]),
      menus: !eqx(menusTuple(draft), menusTuple(saved)),   // v12.10.23 (#2012)
      texts: !!textsState?.dirty,   // v12.10.26 (#T154)
    };
    onStateChange({
      dirty,
      anyDirty: Object.values(dirty).some(Boolean),
      saveAll: async () => {
        try {
          if (dirty.conversation) await saveConversation.mutateAsync(draft);
          if (dirty.processing) await saveProcessing.mutateAsync(draft);
          if (dirty.hold) await saveHold.mutateAsync(draft);
          if (dirty.recording) await saveRecording.mutateAsync(draft);
          if (dirty.menus) await saveMenus.mutateAsync(draft);   // v12.10.23 (#2012)
          if (dirty.texts) await textsState?.save();   // v12.10.26 (#T154)
        } catch { /* mutation error surfaces the failure */ }
      },
      discardAll: () => { setDraft(saved ? structuredClone(saved) : draft); textsState?.discard(); },   // v12.10.26 (#T154)
    });
  }, [draft, saved, onStateChange, textsState]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !draft || !saved) return <div className="settings-loading">Loading Call Handling settings…</div>;
  if (error) return <div className="settings-error">Call Handling settings could not be loaded.</div>;

  const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const patch = (p: Partial<CallHandlingData>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const reset = (keys: (keyof CallHandlingData)[]) => setDraft((d) => {
    if (!d) return d;
    const next = { ...d };
    keys.forEach((k) => { (next as unknown as Record<string, unknown>)[k] = structuredClone((saved as unknown as Record<string, unknown>)[k]); });
    return next;
  });

  const convDirty = !eq(convTuple(draft), convTuple(saved));
  const procDirty = !eq([draft.procMode, draft.procDelay, draft.procSound, draft.filler.proc], [saved.procMode, saved.procDelay, saved.procSound, saved.filler.proc]);
  const holdDirty = !eq([draft.holdMode, draft.holdDelay, draft.holdSound, draft.filler.hold], [saved.holdMode, saved.holdDelay, saved.holdSound, saved.filler.hold]);
  const menusDirty = !eq(menusTuple(draft), menusTuple(saved));   // v12.10.23 (#2012)
  const setMenus = (p: Partial<ChCallMenus>) => setDraft((d) => (d ? { ...d, callMenus: { ...menusOf(d), ...p } } : d));
  const sil = silentOf(draft);   // v12.10.23 (#2029)
  const setSilent = (v: ChSilent) => setMenus({ silent: v });
  const recDirty = !eq([draft.recEnabled, draft.storeTranscript, draft.autoRedact, draft.recRetention, draft.transRetention, draft.voicemailDetails], [saved.recEnabled, saved.storeTranscript, saved.autoRedact, saved.recRetention, saved.transRetention, saved.voicemailDetails]);

  // Filler editors mutate the nested draft.filler immutably.
  const setFiller = (scope: 'proc' | 'hold', mut: (langs: Record<string, Record<string, string[]>>) => Record<string, Record<string, string[]>>) =>
    setDraft((d) => (d ? { ...d, filler: { ...d.filler, [scope]: mut(structuredClone(d.filler[scope] ?? {})) } } : d));

  // Scoped discard for a behavior section — reverts only that scope's mode/delay/sound + filler,
  // so discarding Processing never touches unsaved Hold edits (and vice-versa).
  const resetBehavior = (scope: 'proc' | 'hold') => setDraft((d) => {
    if (!d || !saved) return d;
    const next: CallHandlingData = { ...d, filler: { ...d.filler, [scope]: structuredClone(saved.filler[scope]) } };
    if (scope === 'proc') { next.procMode = saved.procMode; next.procDelay = saved.procDelay; next.procSound = saved.procSound; }
    else { next.holdMode = saved.holdMode; next.holdDelay = saved.holdDelay; next.holdSound = saved.holdSound; }
    return next;
  });

  function previewSound(s: ChSound) {
    if (!s.audioUrl) { showToast('No audio available for this sound yet.'); return; }
    try {
      if (audioRef.current) { audioRef.current.pause(); }
      const a = new Audio(s.audioUrl);
      audioRef.current = a;
      showToast(`▶ Previewing — ${s.name}`);
      a.play().catch(() => showToast('Tap anywhere, then Preview again — the browser blocked autoplay.'));
    } catch { showToast('Preview unavailable for this sound.'); }
  }

  return (
    <div className="organization-panel call-handling-panel">
      {/* ── 1. Conversation flow ─────────────────────────────────────────── */}
      <Section title="Conversation flow" description="How the AI listens, recovers from silence, and lets callers reach a human."
        dirty={convDirty} saving={saveConversation.isPending}
        onSave={() => saveConversation.mutate(draft)} onDiscard={() => { reset(convKeys); setMenus({ silent: menusOf(saved).silent }); }}>
        <div className="subhead">Speech timing</div>
        <div className="fr three">
          {/* ── v12.6.1 (#555): THE FIELD SAID "1 sec" FOR A SETTING NO STAGE OF THE CALL RUNS AS 1. ──
              Tushar set 1000 ms and the hint rendered "1 sec". The resolver floors it at 2
              (Math.Max(2, ms / 1000)) and then EACH GATHER APPLIES ITS OWN FLOOR ON TOP - and they are not
              the same floor, which is the part the original report missed and the part that matters:

                capture steps            Math.Max(sec, 9)   #247, because recalling a DOB takes a beat
                "say that again"         Math.Max(sec, 5)
                wrap-up "anything else?" Math.Max(sec, 5)
                Confirm read-back yes/no raw               -> the resolver's 2
                Confirm correction       raw               -> the resolver's 2

              So one number on this screen produces FOUR different waits, and 1000 ms produced none of them.
              The floors themselves are deliberate and stay; what was wrong is a control that hid them.
              `min` is now 2000 because below that literally nothing on any path changes, and the help says
              what each stage actually does. See #565 for the inconsistency itself. */}
          <Field label="Speech timeout" help={[['What it does', 'The BASE time the AI waits in silence after a prompt before counting it as “no input”. Per-turn timer; resets every time the caller speaks.'], ['Each stage raises it', 'A capture step always waits at least 9 seconds (recalling a date of birth or spelling a name takes a beat). “Say that again” and the closing “anything else?” wait at least 5. Only the Confirm yes/no uses your number as-is. Raising this raises them all; lowering it below those floors changes nothing.'], ['Example', 'Set to 12000 ms → every stage waits 12 seconds, because 12 is above all of them.']]}>
            <NumberField value={draft.speechTimeout} onChange={(v) => patch({ speechTimeout: v })} unit="ms" min={2000} max={30000} step={500} secHint floorMs={2000} />
          </Field>
          {/* v12.10.23 (#2029): Max silence retries + Silence final action are gone from the screen - the Silent caller below replaces
              them (Tushar 10/28, UI-PROTO-MENUS-T174 v1.5 LOCKED). The columns stay stored and are still sent unchanged. */}
        </div>

        <div className="fr three">
          <Field label="End-of-speech" help={[['What it does', 'How long the AI waits after the caller STOPS talking before it replies. Auto lets the system detect the end of speech (~1s, recommended). Set a fixed value if callers spell names or numbers out loud.'], ['Example', 'Auto for normal speech; 3-4 sec when callers spell things (T-U-S-H-A-R) so they are not cut off mid-word.']]}>
            <div className="eos-ctl" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Select
                value={draft.speechEndTimeout === 'auto' ? 'auto' : 'custom'}
                onChange={(v) => patch({ speechEndTimeout: v === 'auto' ? 'auto' : String(eosSec(draft.speechEndTimeout)) })}
                options={EOS_MODE} />
              {draft.speechEndTimeout !== 'auto' && (
                <NumberField value={eosSec(draft.speechEndTimeout)} onChange={(v) => patch({ speechEndTimeout: String(v) })} unit="sec" min={1} max={10} step={1} />
              )}
            </div>
          </Field>
          {/* ── v12.7.3 (#625): SPELLING PAUSE. THE ONE TURN THAT MUST NOT USE `auto`. ──────────────────
              Twilio's end-of-speech detector is tuned for conversational speech and is genuinely better there.
              A spelled name is one-syllable bursts with a breath between each, and it reads the FIRST breath as
              the end of the answer. Not truncation: barge-in means the rest is captured by the NEXT Gather, so
              on 39-0017 "D like David" became the surname and "Edward as like Sam" became the date of birth.
              ONE setting, TWO corrupted fields. Tushar proved the fix with no code on 39-0029.
              One value for every field type, by his decision - letter-by-letter turns only. */}
          <Field label="Spelling pause" help={[['What it does', 'How long the AI waits after each letter when a caller is spelling something out. Spelling is short bursts with a breath between them, and the normal end-of-speech detector treats that first breath as the end of the answer.'], ['Why it is separate', 'It applies ONLY to letter-by-letter turns. The rest of the call keeps End-of-speech above, so conversation stays quick.'], ['Example', '4 sec → “D like David, O like Oscar, E like Echo” arrives as one answer instead of three.']]}>
            <div className="eos-ctl" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Select
                value={draft.spellSpeechEndTimeout === 'auto' ? 'auto' : 'custom'}
                onChange={(v) => patch({ spellSpeechEndTimeout: v === 'auto' ? 'auto' : String(eosSec(draft.spellSpeechEndTimeout)) })}
                options={EOS_MODE} />
              {draft.spellSpeechEndTimeout !== 'auto' && (
                <NumberField value={eosSec(draft.spellSpeechEndTimeout)} onChange={(v) => patch({ spellSpeechEndTimeout: String(v) })} unit="sec" min={1} max={10} step={1} />
              )}
            </div>
          </Field>
          <Field label="Caller input cleanup" help={[['What it does', 'Tidies what the caller says before it is read back and stored — joins spelled-out letters (T-U-S-H-A-R \u2192 Tushar), fixes name casing, and standardizes dates/phone numbers.'], ['Options', 'Raw keeps exactly what the telephony provider returned, with no processing at all. Fast is an instant in-app cleanup (recommended). AI assist runs Fast first and then the model, and keeps the model’s answer only if it still matches the field’s type.'], ['Per field', 'This is the organization default. A single field can override it under AI Agent › Identity extraction, and a workflow step can override that again.']]}>
            <Select value={draft.slotNormalization} onChange={(v) => patch({ slotNormalization: v })} options={SLOTNORM} />
          </Field>
        </div>

        <ListenWaits draft={draft} onChange={(listenWaits) => patch({ listenWaits })} />

        {/* v12.10.23 (#2029, UI-PROTO-MENUS-T174 v1.5 LOCKED): SILENT CALLER - moved here from the workflow's Classify card; it replaces the
            unused Max silence retries / Silence final action (still stored, no longer shown). Until it is saved here the workflow's own
            rule is shown and used (silentFromWorkflow); the first Save of a changed value makes it the practice's setting. */}
        <div className="subhead">Silent caller</div>
        <div className="fr three">
          <Field label="Silent turns in a row" help={[['What it does', 'The whole call: after this many silent turns in a row - the same question or different ones - the caller is not asked anything else and the call goes to the chosen card.'], ['Off', '0 = off - each question keeps its own silence re-ask.']]}>
            <select value={String(sil.turns)} onChange={(e) => setSilent({ ...sil, turns: Number(e.target.value) })}>
              {Array.from(new Set([0, 1, 2, 3, sil.turns])).sort((x, y) => x - y).map((n) => <option key={n} value={n}>{n === 0 ? '0 · off' : n}</option>)}
            </select>
          </Field>
          <Field label="Nothing given yet" help={[['What it does', 'Where the call goes when the caller has said nothing and nothing was captured on the whole call.'], ['Default', 'End call.']]}>
            <Select value={sil.nothingGiven} onChange={(v) => setSilent({ ...sil, nothingGiven: v })} options={SILENT_ENDINGS} handoffOff={!draft.handoffEnabled} />
          </Field>
          <Field label="Something given" help={[['What it does', 'Where the call goes when the caller said or gave something earlier in the call, then went silent.'], ['Default', 'Leave a Message.']]}>
            <Select value={sil.somethingGiven} onChange={(v) => setSilent({ ...sil, somethingGiven: v })} options={SILENT_ENDINGS} handoffOff={!draft.handoffEnabled} />
          </Field>
        </div>
        <p className="sub-note">The whole call: after this many silent turns in a row the call goes to the chosen card. 0 = off (each question keeps its own silence re-ask).{draft.silentFromWorkflow ? ' Shown from the workflow until it is saved here.' : ''}</p>

        <div className="subhead">Smart fallbacks</div>
        <div className="fr">
          <Field label="Operator keyword detection" help={[['What it does', 'Listens for “I want a human”-style escape phrases at any point in the call.'], ['Example', 'Caller says “just give me an operator” → detection triggers → the action below runs.']]}>
            <div className="tog-inline">
              <button type="button" className={`day-toggle${draft.operatorDetect ? '' : ' off'}`} onClick={() => patch({ operatorDetect: !draft.operatorDetect })} aria-label="Toggle operator detection"><span className="knob" /></button>
              <span className="tog-lbl">Listen for escape phrases</span>
            </div>
          </Field>
          <Field label="Operator keyword action" help={[['What it does', 'What the AI does when an operator keyword is detected.'], ['Example', 'Handoff = transfer to a human. Voicemail = route to voicemail.']]}>
            <Select value={draft.operatorAction} onChange={(v) => patch({ operatorAction: v })} options={OPERATOR_ACTIONS} handoffOff={!draft.handoffEnabled} />
          </Field>
        </div>
        <div className="fr">
          <Field label="Operator keywords" help={[['What it does', 'Comma-separated keywords that trigger an immediate escape to the operator action.'], ['Example', 'operator, human, representative, agent']]}>
            <input value={draft.operatorKeywords ?? ''} onChange={(e) => patch({ operatorKeywords: e.target.value })} placeholder="operator, human, representative, agent" />
          </Field>
          <Field label="Very-low-confidence action" help={[['What it does', 'What to do when speech-recognition confidence on a caller utterance is below the workflow’s floor.'], ['When it fires', draft.confidenceFloor != null ? `Below the workflow confidence floor (currently ${draft.confidenceFloor}). The floor is defined in Workflow Designer.` : 'Below the workflow confidence floor (defined in Workflow Designer).']]}>
            <Select value={draft.lowConfAction} onChange={(v) => patch({ lowConfAction: v })} options={LOWCONF_ACTIONS} handoffOff={!draft.handoffEnabled} handoffValue="repeat_handoff" />
          </Field>
        </div>

        <div className="subhead">DTMF (touch-tone)</div>
        <p className="sub-note">A silent keypad backup. Announced only when you choose, so the call stays conversational by default.</p>
        <div className="fr">
          <Field label="Acceptance" help={[['What it does', 'Whether key-presses count at all. On = a press is heard anywhere the workflow supports one, including on a capture step whose field can use a keypad. Off = every press is ignored.'], ['Where it applies', 'On its own this does not make the AI ASK for a key-press. A step opts in under its own “If not captured” switches; this decides whether those are honoured.']]}>
            <Select value={draft.dtmfAccept} onChange={(v) => patch({ dtmfAccept: v })} options={DTMF_ACCEPT} />
          </Field>
          <Field label="Announce timing" help={[['What it does', 'WHERE in the silence ladder the keypad is offered out loud — not how long anything waits.'], ['The ladder', 'greeting nudge → language fallback → keypad menu → keypad re-prompt → voicemail. After silence fallback puts the offer at the keypad-menu stage (today’s behavior). On greeting puts it at the start. Never skips it, and the keypad still works silently if Acceptance is On.']]}>
            <Select value={draft.dtmfAnnounce} onChange={(v) => patch({ dtmfAnnounce: v })} options={DTMF_ANNOUNCE} />
          </Field>
          {/* v12.7.3 (#644): FALLBACK DELAY IS GONE. The silence ladder is STAGE-based, not timed - greeting
              nudge, language fallback, keypad menu, keypad re-prompt, voicemail - and there is nowhere for a
              second silence clock to attach. Tushar's silent call on 2026-09-23 waited 0 ms for it. The column
              stays in the database, unread, because dropping it would make an additive release destructive for
              a value nobody can see. */}
        </div>
      </Section>

      {/* ── 2. Recording ─────────────────────────────────────────────────── */}
      {/* v12.10.23 (#2012, UI-PROTO-MENUS-T174 v1.5 LOCKED): order Conversation flow · Recording · Keypad menus · Hold · Processing. */}
      <Section title="Recording" description="Capture call audio and transcripts, control retention, and mask PHI."
        dirty={recDirty} saving={saveRecording.isPending}
        onSave={() => saveRecording.mutate(draft)} onDiscard={() => reset(['recEnabled', 'storeTranscript', 'autoRedact', 'recRetention', 'transRetention', 'voicemailDetails'])}>
        <RecordingBody d={draft} patch={patch} />
      </Section>

      {/* ── 3. Keypad menus (v12.10.23 #2012 #2028) ─────────────────────── */}
      <Section title="Keypad menus" description="The menus that guide a caller whose own words did not get them to a caller type or a request - every menu of the practice follows these settings."
        dirty={menusDirty} saving={saveMenus.isPending}
        onSave={() => saveMenus.mutate(draft)}
        onDiscard={() => setDraft((x) => (x ? { ...x, callMenus: { ...structuredClone(menusOf(saved)), silent: menusOf(x).silent } } : x))}>
        <KeypadMenusBody d={draft} setMenus={setMenus} />
      </Section>

      {/* ── 4. Hold Behavior ─────────────────────────────────────────────── */}
      <Section title="Hold Behavior" description="What the caller hears while being connected or transferred to a human — silence, a spoken hold message, or a hold sound."
        dirty={holdDirty} saving={saveHold.isPending}
        onSave={() => saveHold.mutate(draft)} onDiscard={() => resetBehavior('hold')}>
        <BehaviorBody scope="hold" soundLabel="Hold sound" mode={draft.holdMode} delay={draft.holdDelay}
          sound={draft.holdSound} sounds={draft.holdSounds} categories={draft.holdCategories} languages={draft.languages}
          filler={draft.filler.hold} onMode={(m) => patch({ holdMode: m })} onDelay={(v) => patch({ holdDelay: v })}
          onSound={(id) => patch({ holdSound: id })} onPreview={previewSound} setFiller={(mut) => setFiller('hold', mut)} />
      </Section>

      {/* ── 5. Processing Behavior ───────────────────────────────────────── */}
      <Section title="Processing Behavior" description="What the caller hears while the assistant is processing a step — silence, a spoken filler phrase, or a thinking sound."
        dirty={procDirty} saving={saveProcessing.isPending}
        onSave={() => saveProcessing.mutate(draft)} onDiscard={() => resetBehavior('proc')}>
        <BehaviorBody scope="proc" soundLabel="Thinking sound" mode={draft.procMode} delay={draft.procDelay}
          sound={draft.procSound} sounds={draft.thinkingSounds} categories={draft.procCategories} languages={draft.languages}
          filler={draft.filler.proc} onMode={(m) => patch({ procMode: m })} onDelay={(v) => patch({ procDelay: v })}
          onSound={(id) => patch({ procSound: id })} onPreview={previewSound} setFiller={(mut) => setFiller('proc', mut)} />
      </Section>

      {/* ── 6. Texts on calls (v12.10.26 #T154, UI-PROTO-T154 v1.0 screen 1) ── */}
      <TextsOnCallsSection onState={setTextsState} />

      {toast && (
        <div className="settings-toast-wrap" role="status" aria-live="polite">
          <div className="settings-toast">{toast}</div>
        </div>
      )}
    </div>
  );
}

const convKeys: (keyof CallHandlingData)[] = ['speechTimeout', 'speechEndTimeout', 'spellSpeechEndTimeout', 'maxSilenceRetries', 'silenceFinalAction', 'operatorDetect', 'operatorKeywords', 'operatorAction', 'lowConfAction', 'slotNormalization', 'dtmfDelay', 'dtmfAccept', 'dtmfAnnounce'];
function convTuple(d: CallHandlingData) {
  return [d.speechTimeout, d.speechEndTimeout, d.spellSpeechEndTimeout, d.maxSilenceRetries, d.silenceFinalAction, d.operatorDetect, d.operatorKeywords, d.operatorAction, d.lowConfAction, d.slotNormalization, d.dtmfDelay, d.dtmfAccept, d.dtmfAnnounce, d.listenWaits, silentOf(d)];   // v12.10.23 (#2029): + Silent caller
}

// ── Section chrome (matches the shared Settings section card + the AI Agent area) ──
function Section(props: { title: string; description: string; dirty: boolean; saving: boolean; onSave: () => void; onDiscard: () => void; children: ReactNode }) {
  return (
    <section className={`settings-section-card${props.dirty ? ' dirty' : ''}`}>
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>{props.title}</h2>
          <div className="settings-actions">
            <button type="button" className="settings-secondary" disabled={!props.dirty || props.saving} onClick={props.onDiscard}>Discard</button>
            <button type="button" className="settings-primary" disabled={!props.dirty || props.saving} onClick={props.onSave}>{props.saving ? 'Saving…' : 'Save section'}</button>
          </div>
        </div>
        <p className="ch-sec-desc">{props.description}</p>
      </header>
      <div className="ch-section-body">{props.children}</div>
    </section>
  );
}

// ── Field wrapper: label + help tooltip + control ──
function Field({ label, help, narrow, tag, children }: { label: string; help?: [string, ReactNode][]; narrow?: boolean; tag?: ReactNode; children: ReactNode }) {
  return (
    <div className={`f${narrow ? ' f-narrow' : ''}`}>
      <label>{label}{help && <HelpTooltip sections={help} />}{tag}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, handoffOff, handoffValue = 'handoff' }: { value: string; onChange: (v: string) => void; options: [string, string][]; handoffOff?: boolean; handoffValue?: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, l]) => (
        <option key={v} value={v} disabled={handoffOff && v === handoffValue}>{l}{handoffOff && v === handoffValue ? ' (Handoff off)' : ''}</option>
      ))}
    </select>
  );
}

// ── Custom-stepper number field (matches .numfield) ──
// v12.6.1 (#555): floorMs is the value the ENGINE will not go below, whatever is stored. When a stored value
// is under it the hint shows what will ACTUALLY happen, not the arithmetic on the number in the box.
function NumberField({ value, onChange, unit, min, max, step = 1, narrow, disabled, secHint, floorMs }: {
  value: number; onChange: (v: number) => void; unit?: string; min: number; max: number; step?: number; narrow?: boolean; disabled?: boolean; secHint?: boolean; floorMs?: number;
}) {
  const clamp = (v: number) => (v < min ? min : v > max ? max : v);
  const bump = (dir: number) => onChange(clamp((Number(value) || 0) + dir * step));
  return (
    <>
      {secHint && (floorMs !== undefined && Number(value) < floorMs
        ? <span className="nf-sec-top nf-sec-floored" title={`Stored as ${value} ms, but no stage of the call waits less than ${msToSec(floorMs)} — and most wait longer. See the help for the per-stage floors.`}>{msToSec(floorMs)} or more</span>
        : <span className="nf-sec-top">{msToSec(value)}</span>)}
      <div className={`numfield${narrow ? ' narrow' : ''}${disabled ? ' nf-disabled' : ''}`}>
        <input type="text" inputMode="numeric" className="nf-input" value={value}
          onChange={(e) => onChange(clamp(Number(e.target.value.replace(/[^0-9.-]/g, '')) || 0))} disabled={disabled} />
        <div className="nf-labels">{unit && <span className="nf-unit">{unit}</span>}</div>
        <div className="nf-steppers">
          <button type="button" className="nf-up" onClick={() => bump(1)} aria-label="Increase" tabIndex={-1} disabled={disabled}><Chevron up /></button>
          <button type="button" className="nf-down" onClick={() => bump(-1)} aria-label="Decrease" tabIndex={-1} disabled={disabled}><Chevron /></button>
        </div>
      </div>
    </>
  );
}

// ── Behavior body (Processing / Hold): pill + start delay + mode reveal ──
function BehaviorBody(props: {
  scope: 'proc' | 'hold'; soundLabel: string; mode: string; delay: number; sound: string | null;
  sounds: ChSound[]; categories: ChFillerCategory[]; languages: ChFillerLang[];
  filler: Record<string, Record<string, string[]>>;
  onMode: (m: string) => void; onDelay: (v: number) => void; onSound: (id: string) => void;
  onPreview: (s: ChSound) => void; setFiller: (mut: (langs: Record<string, Record<string, string[]>>) => Record<string, Record<string, string[]>>) => void;
}) {
  const { scope, soundLabel, mode, categories, languages, filler } = props;
  const pill = (v: string, l: string) => ({ value: v, label: l });
  return (
    <>
      <div className="fr">
        <Field label="Behavior" help={[['What it does', scope === 'proc' ? 'What the caller hears while the assistant is processing a step.' : 'What the caller hears while being connected or transferred to a human.'], ['Default', 'Silence — nothing plays unless you choose otherwise.']]}>
          <div className="seg-group">
            {[pill('silence', 'Silence'), pill('filler', 'Filler phrase'), pill('sound', soundLabel)].map((o) => (
              <button key={o.value} type="button" className={`seg${mode === o.value ? ' on' : ''}`} onClick={() => props.onMode(o.value)}><span className="seg-dot" />{o.label}</button>
            ))}
          </div>
        </Field>
        <Field narrow label="Start delay" help={[['What it does', `How long to wait into a ${scope === 'proc' ? 'processing step' : 'hold/connect'} before the chosen behavior begins.`], ['Example', '1500 ms = nothing for fast moments, but the behavior kicks in on longer waits.']]}>
          <NumberField value={props.delay} onChange={props.onDelay} unit="ms" min={0} max={10000} step={100} narrow secHint />
        </Field>
      </div>

      {mode === 'sound' && (
        <>
          <div className="subhead">{soundLabel}</div>
          <p className="ts-hint"><PlayIcon /> Click to preview each sound.</p>
          <div className="ts-grid">
            {props.sounds.map((s) => (
              <button key={s.code} type="button" className={`ts-opt${props.sound === s.code ? ' sel' : ''}`} onClick={() => props.onSound(s.code)}>
                <span className="ts-radio" />
                <span className="ts-meta"><span className="ts-name">{s.name}</span><span className="ts-desc">{s.description}</span></span>
                <span className="ts-play" onClick={(e) => { e.stopPropagation(); props.onPreview(s); }} aria-label={`Preview ${s.name}`}><PlayIcon /></span>
              </button>
            ))}
            {props.sounds.length === 0 && <div className="fp-empty">No {soundLabel.toLowerCase()}s are available yet.</div>}
          </div>
        </>
      )}

      {mode === 'filler' && (
        <FillerEditor scope={scope} categories={categories} languages={languages} filler={filler} setFiller={props.setFiller} />
      )}

      {mode === 'silence' && (
        <div className="behav-silence-note"><MuteIcon /><div>The line stays quiet during {scope === 'proc' ? 'processing' : 'connect/transfer'}. Choose <b>Filler phrase</b> or <b>{soundLabel}</b> above to fill the wait.</div></div>
      )}
    </>
  );
}

// ── Filler phrase editor: language tabs + platform-seeded categories + editable phrase lists ──
function FillerEditor({ categories, languages, filler, setFiller }: {
  scope: 'proc' | 'hold'; categories: ChFillerCategory[]; languages: ChFillerLang[];
  filler: Record<string, Record<string, string[]>>;
  setFiller: (mut: (langs: Record<string, Record<string, string[]>>) => Record<string, Record<string, string[]>>) => void;
}) {
  // v12.2.40.0 (#230): dedupe by code (the settings payload was returning duplicate rows -> repeated tabs) and
  // label each tab with a friendly language name instead of a code-ish value.
  const uniqueLangs = languages.filter((l, i, arr) => arr.findIndex((x) => x.code === l.code) === i);
  const LANG_NAMES: Record<string, string> = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', zh: 'Chinese', ar: 'Arabic', pt: 'Portuguese', ru: 'Russian', hi: 'Hindi' };
  const langLabel = (l: ChFillerLang) => LANG_NAMES[(l.code ?? '').toLowerCase()] ?? l.name ?? l.code;
  const codes = uniqueLangs.map((l) => l.code);
  const [active, setActive] = useState(codes[0] ?? '');
  const lang = codes.includes(active) ? active : (codes[0] ?? '');
  const phrasesFor = (cat: string) => filler?.[lang]?.[cat] ?? [];

  const editPhrase = (cat: string, i: number, val: string) => setFiller((langs) => {
    const byCat = { ...(langs[lang] ?? {}) };
    const arr = [...(byCat[cat] ?? [])]; arr[i] = val; byCat[cat] = arr;
    return { ...langs, [lang]: byCat };
  });
  const addPhrase = (cat: string) => setFiller((langs) => {
    const byCat = { ...(langs[lang] ?? {}) };
    byCat[cat] = [...(byCat[cat] ?? []), ''];
    return { ...langs, [lang]: byCat };
  });
  const delPhrase = (cat: string, i: number) => setFiller((langs) => {
    const byCat = { ...(langs[lang] ?? {}) };
    byCat[cat] = (byCat[cat] ?? []).filter((_, j) => j !== i);
    return { ...langs, [lang]: byCat };
  });

  if (codes.length === 0) return <p className="sub-note">No languages are enabled for your account yet.</p>;
  return (
    <>
      <div className="subhead">Filler phrases</div>
      <p className="fp-hint">The categories below are set for you — you write the phrases, not the categories. The assistant matches the phrase to what it's doing and rotates through your wording; if a category has no phrases it falls back to <b>General</b>, then to silence.</p>
      <div className="lang-tabs">
        {uniqueLangs.map((l) => (
          <button key={l.code} type="button" className={`lang-tab${lang === l.code ? ' on' : ''}`} onClick={() => setActive(l.code)}>{langLabel(l)} &middot; {(l.code ?? '').toUpperCase()}</button>
        ))}
      </div>
      <div className="fp-cats">
        {categories.map((cat) => {
          const isGeneral = cat.key === 'general';
          const phrases = phrasesFor(cat.key);
          return (
            <div key={cat.key} className={`fp-cat${isGeneral ? ' fp-general' : ''}`}>
              <div className="fp-cat-head">
                <span className="fp-cat-name">{cat.label}{isGeneral && <span className="fp-req">required backstop</span>}</span>
                <span className="fp-cat-desc">{cat.hint}</span>
              </div>
              <div className="fp-rows">
                {phrases.length === 0 && <div className="fp-empty">No phrases yet{isGeneral ? ' — General is the fallback and should not be empty.' : '.'}</div>}
                {phrases.map((p, i) => (
                  <div key={i} className="fp-row">
                    <input className="fp-input" value={p} placeholder="Type a phrase the assistant will speak…" onChange={(e) => editPhrase(cat.key, i, e.target.value)} />
                    <button type="button" className="fp-del" onClick={() => delPhrase(cat.key, i)} aria-label="Remove phrase"><CloseIcon /></button>
                  </div>
                ))}
              </div>
              <button type="button" className="fp-add" onClick={() => addPhrase(cat.key)}>+ Add phrase</button>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Recording body: two masters + redaction (entitlement-gated) + retention + disclosure pointer ──
function RecordingBody({ d, patch }: { d: CallHandlingData; patch: (p: Partial<CallHandlingData>) => void }) {
  const audioOff = !d.recEnabled;
  const transOff = !d.storeTranscript;
  const redactBlocked = transOff || !d.redactEntitled;
  const redactNote = !d.redactEntitled
    ? <span className="tog-note">Not enabled for your account.</span>
    : transOff ? <span className="tog-note">Turn on <b>Keep call transcript</b> to use redaction.</span> : null;
  return (
    <>
      <div className={`rec-card${d.recEnabled ? ' on' : ''}`}>
        <div className="rec-card-main">
          <div className="rec-card-txt">
            <b>Record call audio</b>
            <HelpTooltip sections={[['What it does', 'Master switch for capturing the call audio recording on this number.'], ['Example', 'Off → no audio is captured. The text transcript is unaffected.']]} />
            <span className="rec-card-sub">{d.recEnabled ? 'Call audio on this number is being recorded.' : 'Call audio is not being recorded. Transcripts are unaffected.'}</span>
          </div>
          <div className="rec-card-right">
            <span className={`rec-pill${d.recEnabled ? ' on' : ''}`}><span className="dot" /> Recording {d.recEnabled ? 'on' : 'off'}</span>
            <button type="button" className={`day-toggle rec-toggle${d.recEnabled ? '' : ' off'}`} onClick={() => patch({ recEnabled: !d.recEnabled })} aria-label="Toggle audio recording"><span className="knob" /></button>
          </div>
        </div>
      </div>

      <div className="rec-body">
        <div className="fr">
          <Field label="Keep call transcript" help={[['What it does', 'Master switch for keeping the text transcript of each call. Independent of audio recording.'], ['Example', 'Off → the transcript is discarded after the call and nothing text is stored.']]}>
            <div className="tog-inline">
              <button type="button" className={`day-toggle${d.storeTranscript ? '' : ' off'}`} onClick={() => patch({ storeTranscript: !d.storeTranscript })} aria-label="Toggle keep transcript"><span className="knob" /></button>
              <span className="tog-lbl">Keep call transcript</span>
            </div>
          </Field>
          {/* v12.10.15 (#1666, LOCKED S170): how a voicemail's details are read. */}
          <Field label="Voicemail details" help={[['What it does', 'How the details in a voicemail reach the message. Smart (AI): after the call, one AI read fills every field it can - name, date of birth, callback number, the reason - each marked \u201cfrom voicemail\u201d; it never replaces what the caller said in the conversation. Basic (keywords): only the request type, from fixed words, and no AI cost.'], ['Default', 'Smart.']]}>
            <select value={d.voicemailDetails === 'basic' ? 'basic' : 'smart'} onChange={(e) => patch({ voicemailDetails: e.target.value })}>
              <option value="smart">Smart (AI)</option>
              <option value="basic">Basic (keywords)</option>
            </select>
          </Field>
          <Field label="Transcript retention" help={[['What it does', 'How long text transcripts are kept before automatic deletion. Applies only when “Keep call transcript” is on.'], ['When it fires', 'A scheduled purge job removes transcripts older than this window.']]}>
            <NumberField value={d.transRetention} onChange={(v) => patch({ transRetention: v })} unit="days" min={1} max={3650} step={1} disabled={transOff} />
          </Field>
        </div>
        <div className="fr">
          <Field label="Auto-redact PHI" help={[['What it does', 'Automatically masks protected health information (names, DOB, IDs) in stored transcripts. Transcript text only; audio is not redacted.'], ['Example', '“My name is Jane Doe” → stored as “My name is [REDACTED]”.']]}>
            <div className="tog-inline">
              <button type="button" className={`day-toggle${d.autoRedact && !redactBlocked ? '' : ' off'}`} onClick={() => { if (!redactBlocked) patch({ autoRedact: !d.autoRedact }); }} aria-label="Toggle redaction" disabled={redactBlocked}><span className="knob" /></button>
              <span className="tog-lbl">Mask PHI in transcripts</span>
              {redactNote}
            </div>
          </Field>
          <Field label="Recording retention" help={[['What it does', 'How long call audio recordings are kept before automatic deletion. Applies only when “Record call audio” is on.'], ['When it fires', 'A scheduled purge job removes recordings older than this window.']]}>
            <NumberField value={d.recRetention} onChange={(v) => patch({ recRetention: v })} unit="days" min={1} max={3650} step={1} disabled={audioOff} />
          </Field>
        </div>

        <div className="subhead">Disclosure announcement</div>
        <div className="xref-note"><ExternalIcon /><div><b>The recording disclosure is defined in Workflow Designer.</b> Open the <em>Disclosure</em> card in Workflow to write the per-language message and turn the announcement on or off. It plays before the master greeting on every call.</div></div>
      </div>
    </>
  );
}

// ── helpers + inline icons ──
function msToSec(ms: number | string): string {
  const n = Number(ms);
  if (!isFinite(n)) return '';
  const s = n / 1000;
  return (Number.isInteger(s) ? s : s.toFixed(s < 1 ? 2 : 1)) + ' sec';
}
function Chevron({ up }: { up?: boolean }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{up ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}</svg>;
}
function PlayIcon() { return <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>; }
function MuteIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>; }
function ExternalIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14L21 3" /></svg>; }

/* ══ v12.10.8.1 (#1232): LISTENING WAIT BY KIND OF ANSWER ═══════════════════════════════════════════════════════════
   Tushar 10/5: "why do i have to run the script and not edit settings from ui?" and, on the prototype, one setting per
   row for every language with "Set per language" (v1.2 APPROVED). Folded under Advanced - most practices leave it alone.
   A practice setting, not part of the workflow template (his call). Storage stays per language (the engine reads
   eos.<kind> per language); "all languages" is simply the value most languages share. */
const LISTEN_ROWS: { kind: string; label: string; sub: string }[] = [
  { kind: 'text', label: 'Free-text answers', sub: 'The reason for the visit, a note - any field of type Text' },
  { kind: 'sentence', label: 'Sentences', sub: 'A correction ("change the date to…"), the text offer, "anything else?"' },
  { kind: 'readback', label: 'Read-back answer', sub: 'The caller\'s reply to "Just to confirm, I have…" - usually "yes"' },
];

function ListenWaits({ draft, onChange }: { draft: CallHandlingData; onChange: (w: Record<string, Record<string, string>>) => void }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const langs = draft.languages?.length ? draft.languages : [{ code: 'en', name: 'English' }];
  const waits = draft.listenWaits ?? {};
  const valueOf = (kind: string, lang: string) => (waits[kind] ?? {})[lang] ?? '';
  const mainOf = (kind: string) => {
    const counts = new Map<string, number>();
    for (const l of langs) counts.set(valueOf(kind, l.code), (counts.get(valueOf(kind, l.code)) ?? 0) + 1);
    let best = valueOf(kind, langs[0].code); let n = -1;
    for (const l of langs) { const v = valueOf(kind, l.code); const c = counts.get(v) ?? 0; if (c > n) { best = v; n = c; } }
    return best;
  };
  const setLang = (kind: string, lang: string, v: string) => {
    const row = { ...(waits[kind] ?? {}) };
    if (v === '') delete row[lang]; else row[lang] = v;
    onChange({ ...waits, [kind]: row });
  };
  const setMain = (kind: string, v: string) => {
    const old = mainOf(kind);
    const row: Record<string, string> = { ...(waits[kind] ?? {}) };
    for (const l of langs) if (valueOf(kind, l.code) === old) { if (v === '') delete row[l.code]; else row[l.code] = v; }
    onChange({ ...waits, [kind]: row });
  };
  const reset = (kind: string) => onChange({ ...waits, [kind]: {} });
  const mode = (v: string) => (v === '' ? 'default' : v === 'auto' ? 'auto' : 'custom');
  const secs = (v: string, dflt: number) => { const n = parseInt(v, 10); return isNaN(n) ? dflt : Math.min(10, Math.max(1, n)); };

  return (
    <details className="lw-adv">
      <summary>Advanced - listening wait by kind of answer</summary>
      <p className="lw-intro">How long the AI keeps listening after the caller pauses, for answers that are usually more than one word. Auto ends the turn at the first natural pause - quickest, but a caller who pauses mid-sentence can be cut off. Seconds wait longer.</p>
      <table className="lw-table">
        <thead><tr><th>Kind of answer</th><th>All languages</th><th /></tr></thead>
        <tbody>
          {LISTEN_ROWS.map((r) => {
            const dflt = draft.listenDefaults?.[r.kind] ?? 3;
            const main = mainOf(r.kind);
            const differs = langs.filter((l) => valueOf(r.kind, l.code) !== main);
            return (
              <tr key={r.kind}>
                <td><b>{r.label}</b><small>{r.sub}</small></td>
                <td>
                  <div className="lw-ctl">
                    <select value={mode(main)} onChange={(e) => setMain(r.kind, e.target.value === 'default' ? '' : e.target.value === 'auto' ? 'auto' : String(secs(main, dflt)))}>
                      <option value="default">Platform default ({dflt} sec)</option>
                      <option value="auto">Auto</option>
                      <option value="custom">Custom</option>
                    </select>
                    {mode(main) === 'custom' && <NumberField value={secs(main, dflt)} onChange={(v) => setMain(r.kind, String(v))} unit="sec" min={1} max={10} step={1} />}
                  </div>
                  <div className="lw-meta">Platform default <b>{dflt} sec</b>{differs.map((l) => <span key={l.code} className="lw-chip">{l.name} differs</span>)}</div>
                  {langs.length > 1 && (
                    <button type="button" className="lw-link" onClick={() => setOpen((o) => ({ ...o, [r.kind]: !o[r.kind] }))}>{open[r.kind] ? 'Hide per language' : 'Set per language'}</button>
                  )}
                  {open[r.kind] && langs.length > 1 && (
                    <div className="lw-langs">
                      {langs.map((l) => {
                        const v = valueOf(r.kind, l.code);
                        const same = v === main;
                        return (
                          <div key={l.code} className="lw-lang">
                            <span>{l.name}</span>
                            <div className="lw-ctl">
                              <select value={same ? 'same' : mode(v)} onChange={(e) => {
                                const m = e.target.value;
                                setLang(r.kind, l.code, m === 'same' ? main : m === 'default' ? '' : m === 'auto' ? 'auto' : String(secs(v || main, dflt)));
                              }}>
                                <option value="same">Same as all</option>
                                <option value="default">Platform default ({dflt} sec)</option>
                                <option value="auto">Auto</option>
                                <option value="custom">Custom</option>
                              </select>
                              {!same && mode(v) === 'custom' && <NumberField value={secs(v, dflt)} onChange={(n) => setLang(r.kind, l.code, String(n))} unit="sec" min={1} max={10} step={1} />}
                            </div>
                          </div>
                        );
                      })}
                      <div className="lw-note">"Same as all" follows the row above. Only languages the practice has turned on are listed; a new one starts on "Same as all".</div>
                    </div>
                  )}
                </td>
                <td><button type="button" className="lw-link" onClick={() => reset(r.kind)}>Reset</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="lw-fields"><span className="lw-fields-lbl">Field answers - set by the platform</span>
        {(draft.fieldWaits ?? []).map((f) => <span key={f.label} className="lw-pill">{f.label} {f.seconds} sec</span>)}
      </div>
    </details>
  );
}

/* ══ v12.10.23 (#2012 #2028, UI-PROTO-MENUS-T174 v1.5 LOCKED 10/29): KEYPAD MENUS ═══════════════════════════════════════════════
   Every keypad menu of the practice (the caller-type menu and each request menu) follows these keys. A key is a digit, * or #;
   two actions never share one (a key taken by another row is greyed out). The line is optional per language - blank = the
   Template default. Stored in tenant_settings["callMenus"] (CallMenuSettings). */
const MENU_KEY_CHOICES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
const keyWord = (k: string) => (k === '*' ? 'star' : k === '#' ? 'pound' : k);
const MENU_ROWS: { id: 'repeat' | 'mainMenu' | 'person'; label: string; sub?: string; line: (k: string) => string }[] = [
  { id: 'repeat', label: 'Repeat this menu', line: (k) => `To hear this again, press ${keyWord(k)}.` },
  { id: 'mainMenu', label: 'Back to the main menu', sub: 'the caller-type menu; not said on the main menu itself', line: (k) => `To go back to the main menu, press ${keyWord(k)}.` },
  { id: 'person', label: 'Speak with someone', sub: 'only while handoff is on (AI Agent › Handoff)', line: (k) => `To speak with someone, press ${keyWord(k)}.` },
];

function KeypadMenusBody({ d, setMenus }: { d: CallHandlingData; setMenus: (p: Partial<ChCallMenus>) => void }) {
  const m = menusOf(d);
  const LANG_NAMES: Record<string, string> = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', zh: 'Chinese', ar: 'Arabic', pt: 'Portuguese', ru: 'Russian', hi: 'Hindi' };
  const langs = (d.languages?.length ? d.languages : [{ code: 'en', name: 'English' }]).filter((l, i, arr) => arr.findIndex((x) => x.code === l.code) === i);
  const langLabel = (l: ChFillerLang) => LANG_NAMES[(l.code ?? '').toLowerCase()] ?? l.name ?? l.code;
  const [active, setActive] = useState(langs[0]?.code ?? 'en');
  const lang = langs.some((l) => l.code === active) ? active : (langs[0]?.code ?? 'en');
  const keys: Record<string, string> = { repeat: m.repeat.key, mainMenu: m.mainMenu.key, person: m.person.key, reserved: m.reservedKey };
  const usedBy = (self: string) => Object.entries(keys).filter(([id]) => id !== self).map(([, k]) => k);
  const setKey = (id: 'repeat' | 'mainMenu' | 'person', p: Partial<ChMenuKey>) => setMenus({ [id]: { ...m[id], ...p } } as Partial<ChCallMenus>);
  const setLine = (id: 'repeat' | 'mainMenu' | 'person', text: string) => {
    const line = { ...(m[id].line ?? {}) };
    if (text === '') delete line[lang]; else line[lang] = text;
    setKey(id, { line });
  };
  const maxChoices = Array.from(new Set([2, 3, 4, m.shortMenuMax])).sort((x, y) => x - y);
  return (
    <>
      <div className="subhead">When a menu plays</div>
      <p className="sub-note">Only when the caller’s own words did not get them there. <b>First the clarify line</b> (the card’s re-ask line, no keys - e.g. “Are you a patient, or calling from an office?”), <b>then the keypad prompt</b> (say or press) only if that is still not captured. The words of each menu are on its card (Classify, each caller type) - <i>Keypad menu</i> row.</p>

      <div className="subhead">Menu keys · every keypad menu</div>
      {langs.length > 1 && (
        <div className="lang-tabs">
          {langs.map((l) => (
            <button key={l.code} type="button" className={`lang-tab${lang === l.code ? ' on' : ''}`} onClick={() => setActive(l.code)}>{langLabel(l)} &middot; {(l.code ?? '').toUpperCase()}</button>
          ))}
        </div>
      )}
      {MENU_ROWS.map((r) => {
        const k = m[r.id];
        const used = usedBy(r.id);
        const sub = r.id === 'person' && !d.handoffEnabled ? `${r.sub} - off now, so not said` : r.sub;
        return (
          <div key={r.id} className="km-row">
            <select value={k.key} onChange={(e) => setKey(r.id, { key: e.target.value })} aria-label={`Key for ${r.label}`}>
              {MENU_KEY_CHOICES.map((x) => <option key={x} value={x} disabled={used.includes(x)}>{x}</option>)}
            </select>
            <div className="km-what"><b>{r.label}</b>{sub && <small>{sub}</small>}</div>
            <input value={k.line?.[lang] ?? ''} onChange={(e) => setLine(r.id, e.target.value)}
              placeholder={lang.toLowerCase().startsWith('en') ? r.line(k.key) : 'Template default'} aria-label={`What the caller hears for ${r.label}`} />
            <button type="button" className={`day-toggle${k.on ? '' : ' off'}`} onClick={() => setKey(r.id, { on: !k.on })} aria-label={`Toggle ${r.label}`}><span className="knob" /></button>
          </div>
        );
      })}
      <div className="km-row">
        <select value={m.reservedKey} onChange={(e) => setMenus({ reservedKey: e.target.value })} aria-label="Reserved key">
          {MENU_KEY_CHOICES.map((x) => <option key={x} value={x} disabled={usedBy('reserved').includes(x)}>{x}</option>)}
        </select>
        <div className="km-what"><b>Reserved</b><small>for collecting caller input later - not used in menus</small></div>
        <span />
        <span />
      </div>
      <p className="km-note">Each key can be changed (a digit, * or #); two actions cannot share a key; a request key cannot use one of these. A blank line uses the Template default.</p>

      <div className="subhead">Short menus · every request menu</div>
      <div className="tog-inline">
        <button type="button" className={`day-toggle${m.shortMenus ? '' : ' off'}`} onClick={() => setMenus({ shortMenus: !m.shortMenus })} aria-label="Toggle short menus"><span className="knob" /></button>
        <span className="tog-lbl">Read at most</span>
        <select className="km-num" value={String(m.shortMenuMax)} disabled={!m.shortMenus} onChange={(e) => setMenus({ shortMenuMax: Number(e.target.value) })} aria-label="Requests read">
          {maxChoices.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="tog-lbl">requests, then “For anything else, press [key]”.</span>
      </div>
      <p className="km-note">The first ones in the workflow order are read; the others still work when said or pressed. A caller type with one request (plus Something else) never gets a menu.</p>
    </>
  );
}
